/*!
 * draw.js — draw(): the ink an animated object leaves behind
 * © 2025 Rob Canning — GPLv3
 *
 *     draw(color:#ffcc00, width:2, min:1.5, fade:6, max:400)
 *
 * Put it on an element that something else is moving — trans() between
 * waypoints, o2p() along a path, rotate() around a point, propagate() over a
 * whole cluster — and the element leaves a line where it has been. A random
 * trans with a draw() on it is a little drawing bot.
 *
 * ── The unit that matters ────────────────────────────────────────────────
 * The animation cues do NOT move the element: they put a CSS transform on a
 * wrapper <g> around it. So the element's own geometry never changes, and
 * asking it where it is means reading the live matrix, not its box. That read
 * has to be the COMPOSED one from worldCoords.js — the score SVG's screen CTM
 * inverted against the element's. One CTM alone carries the viewBox scale,
 * which is invisible on a score that draws at 1x and wrong on every other one:
 * the ink would land somewhere else entirely, scaled away from the object that
 * drew it. That is why the arithmetic lives in worldCoords and not here.
 *
 * The element's centre in its OWN coordinates is measured once, at the start.
 * It cannot change while a wrapper transform is what is moving — so the
 * per-frame cost is one getScreenCTM per pen, plus one inverse per SVG root
 * per frame, and no getBBox at all.
 *
 * ── Where the ink goes ───────────────────────────────────────────────────
 * Into a <g class="oscilla-draw-layer"> appended to the score (or page) SVG
 * that the element belongs to, in that SVG's user space — which is exactly
 * what the composed matrix hands us, so the points go in unconverted. It is
 * generated, ephemeral and never written to a file: rewinding clears it.
 *
 * Deliberately NOT the session drawing layer. Those strokes are a performer's
 * — authored by hand, owned by a name, erasable and draggable, and saved in
 * the project's annotations sidecar. A bot filling that file up on every
 * playthrough would bury the rehearsal marks it exists for.
 */

import { buildPathD, distance, simplifyPoints } from "../system/strokePath.js";
import { localMatrixFrom } from "../system/worldCoords.js";
import { parseDrawConfig, stepIndex, pickCycled, fadeOpacity, trimToMax, isJump, JUMP_FLOOR } from "./drawConfig.js";
import { parseCueToAST } from "../parser/parser.js";
import { splitCueId } from "../parser/cueUtils.js";
import { runNext } from "./cueNext.js";
import { secs, secPerBeat } from "./beats.js";
import { onReset } from "../system/teardown.js";
import { transport } from "../system/transportState.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// A run is one <path>: the trail is cut into runs so that colour and width can
// step along it, and so that fading ink can be thrown away a piece at a time.
// How many steps a fading tail decays in. The tail is cut into this many
// <path>s so the far end can go before the near end — and NOT one per handful
// of points: with a long fade nothing is thrown away for a long time, so a
// fixed chunk of points would make the node count grow with the fade. Cutting
// the SAME tail into a fixed number of pieces makes the cost depend on how
// many pens there are, not on how long the ink lingers.
const FADE_STEPS = 8;
const FADE_CHUNK_MIN = 6;         // never finer than this, however short the trail

/** Points per run for a fading pen: its whole tail, in FADE_STEPS pieces. */
function fadeChunkPoints(cfg) {
    // max: caps the tail directly. Without one, fall back to a tail of a few
    // hundred points, which is what a trim-less pen settles at in practice.
    const tail = cfg.max > 0 ? cfg.max : 240;
    return Math.max(FADE_CHUNK_MIN, Math.ceil(tail / FADE_STEPS));
}

// Opacity is written per run per frame while fading. Over a long fade the
// value barely moves between frames — fade:95 changes it by 0.0002 a frame —
// so writing it every time is thousands of style invalidations a second for
// a change nobody can see. Write only when it has moved this far.
const FADE_OPACITY_STEP = 0.02;
// A run is also cut at this length when nothing is fading. Without it a long
// permanent line is ONE path whose `d` grows all piece: every added point
// rewrites the whole string, so a ten-minute drawing ends up rewriting
// hundreds of kilobytes a frame to add two numbers to the end. Cut into
// pieces, only the newest one is ever rewritten and the rest sit still.
const RUN_MAX_POINTS = 200;
const SIMPLIFY_TOLERANCE = 0.6;   // world units; see strokePath.simplifyPoints

export { parseDrawConfig, stepIndex, pickCycled, fadeOpacity, trimToMax, isJump };

// ─────────────────────────────────────────────────────────────
//  The layer
// ─────────────────────────────────────────────────────────────

/**
 * The frame a pen's ink belongs in — the drawing element's, not the score's.
 *
 * An element inside a pin() is held on screen by a compensating transform on
 * its wrapper while the score scrolls underneath. Ink laid down in the root's
 * frame does not get that transform, so it stays with the score and the two
 * come apart the moment the score moves: a held group standing still while
 * its own trails slid away under a drag. Ink goes inside the same wrapper
 * instead, and is recorded in that wrapper's space, so the two move as one.
 *
 * Only pin wrappers count. The oscilla-anim wrappers carry the element's OWN
 * rotation and scaling, which the ink must not inherit — a trail is drawn
 * precisely because the object turns and the ink does not.
 */
function frameFor(el) {
    return el?.closest?.("g.pin-wrapper") || el?.ownerSVGElement || null;
}

function ensureLayer(container) {
    if (!container) return null;
    let layer = container.querySelector(":scope > g.oscilla-draw-layer");
    if (layer) return layer;
    layer = document.createElementNS(SVG_NS, "g");
    layer.setAttribute("class", "oscilla-draw-layer");
    // Generated, so nothing that walks the score for authored content should
    // claim it; and it must never swallow a click meant for the score.
    layer.setAttribute("data-oscilla-generated", "1");
    layer.style.pointerEvents = "none";
    container.appendChild(layer);
    return layer;
}

/** The colour to draw in when the cue did not name one: the object's own. */
function inheritedColour(el) {
    try {
        const cs = getComputedStyle(el);
        for (const v of [cs.stroke, cs.fill]) {
            if (v && v !== "none" && !/^rgba\(0,\s*0,\s*0,\s*0\)$/.test(v)) return v;
        }
    } catch { /* not rendered */ }
    return "#3b82f6";
}

// ─────────────────────────────────────────────────────────────
//  The pens
// ─────────────────────────────────────────────────────────────

const pens = new Map();     // element → pen
let rafId = 0;

/**
 * Where a pen's runs go. anchor:playhead pens get a group of their own, which
 * is translated to the playhead every frame: their points are stored relative
 * to it. A reset empties the layer, so the group is re-made if it went.
 */
function inkParent(pen) {
    if (pen.cfg.anchor !== "playhead") return pen.layer;
    return anchoredGroup(pen.layer);
}

// All anchor:playhead ink on a layer shares ONE group, moved once a frame
// (frame()), rather than a group per pen each moved separately.
function anchoredGroup(layer) {
    let g = layer.querySelector(":scope > g.oscilla-draw-anchored");
    if (!g) {
        g = document.createElementNS(SVG_NS, "g");
        g.setAttribute("class", "oscilla-draw-anchored");
        layer.appendChild(g);
        g._shift = null;
    }
    return g;
}

/**
 * How far PLAYBACK has carried the playhead, in score units (horizontal).
 * Only the transport running advances it: dragging or scrubbing the score,
 * a seek, a marker jump or a restored position move the playhead too, but
 * they move the score under the ink rather than streak it — so there the
 * anchored ink must travel with the score like everything else. Updated once
 * per frame (see frame()).
 */
let playbackShift = 0;
let lastPlayheadX = null;
const PLAYBACK_STEP_MAX = 200;   // score units per frame; a bigger step is a jump, not playback

function trackPlayback() {
    const x = Number(transport.playheadX);
    if (!Number.isFinite(x)) return;
    // a zoom() carrying the playhead to a new place is not playback either
    if (lastPlayheadX != null && transport.isPlaying && !window._zoomCarryingPlayhead) {
        const dx = x - lastPlayheadX;
        if (Math.abs(dx) <= PLAYBACK_STEP_MAX) playbackShift += dx;
    }
    lastPlayheadX = x;
}

function playheadOffset() {
    return playbackShift;
}

function newRun(pen, startPoint) {
    const i = pen.step;
    const run = {
        points: startPoint ? [startPoint] : [],
        bornAt: performance.now(),
        path: document.createElementNS(SVG_NS, "path"),
        dirty: true,
    };
    const colour = pen.cfg.colors ? pickCycled(pen.cfg.colors, i) : pen.colour;
    run.path.setAttribute("fill", "none");
    run.path.setAttribute("stroke", String(colour));
    run.path.setAttribute("stroke-width", String(pickCycled(pen.cfg.widths, i)));
    run.path.setAttribute("stroke-linecap", "round");
    run.path.setAttribute("stroke-linejoin", "round");
    if (pen.cfg.dash) run.path.setAttribute("stroke-dasharray", pen.cfg.dash);
    run.path.setAttribute("opacity", String(pen.cfg.opacity));
    if (pen.uid) run.path.dataset.drawUid = pen.uid;
    inkParent(pen).appendChild(run.path);
    pen.runs.push(run);
    return run;
}

function addPoint(pen, x, y, now, lift = false) {
    const point = { x, y };
    let run = pen.runs[pen.runs.length - 1];

    // A new run when the colour/width step advances, or when a fading trail
    // has filled its chunk. Carry the previous point over so the join has no
    // gap in it — unless the pen is lifting, which is the whole point: the
    // new line must NOT reach back to where the object was.
    const step = stepIndex(now - pen.startedAt, pen.cfg.seg);
    const stepped = step !== pen.step && (pen.cfg.colors?.length > 1 || pen.cfg.widths.length > 1);
    const chunked = run && run.points.length >= (pen.cfg.fade > 0 ? fadeChunkPoints(pen.cfg) : RUN_MAX_POINTS);

    if (!run || stepped || chunked || lift) {
        // A full run will not be added to again: keep only the points its curve
        // needs. Without this a long continuous trail is stored point by point
        // — about one per frame per pen — until the pen lifts, which on a
        // long piece may be never, and every repaint pays for all of them.
        if (run && run.points.length > 2) {
            run.points = simplifyPoints(run.points, SIMPLIFY_TOLERANCE);
            run.path.setAttribute("d", buildPathD(run.points));
            run.dirty = false;
            pen.points = pen.runs.reduce((n, r) => n + r.points.length, 0);
        }
        if (run) archiveRun(pen, run);
        pen.step = step;
        run = newRun(pen, (run && !lift) ? run.points[run.points.length - 1] : null);
    }

    run.points.push(point);
    run.dirty = true;
    pen.last = point;
    pen.points++;
}

function repaint(pen, now) {
    // Fading runs expire from the front; everything else only needs its `d`
    // rewritten when a point was added to it.
    if (pen.cfg.fade > 0) {
        while (pen.runs.length) {
            const run = pen.runs[0];
            if (fadeOpacity(now - run.bornAt, pen.cfg.fade, pen.cfg.opacity) > 0) break;
            run.path.remove();
            pen.points -= run.points.length;
            pen.runs.shift();
        }
        for (const run of pen.runs) {
            const o = fadeOpacity(now - run.bornAt, pen.cfg.fade, pen.cfg.opacity);
            if (run._op === undefined || o <= 0 || Math.abs(o - run._op) >= FADE_OPACITY_STEP) {
                run.path.setAttribute("opacity", String(o));
                run._op = o;
            }
        }
    }

    const before = pen.cfg.max > 0 ? [...pen.runs] : null;
    if (trimToMax(pen.runs, pen.cfg.max)) {
        // whole runs trimmed off the front take their paths with them: left,
        // they were never trimmed or faded again, and max: held nothing
        for (const run of before) if (!pen.runs.includes(run)) run.path.remove();
        pen.points = pen.runs.reduce((n, r) => n + r.points.length, 0);
        for (const run of pen.runs) run.dirty = true;
        // a run can be emptied by the trim
        for (let i = pen.runs.length - 1; i >= 0; i--) {
            if (!pen.runs[i].points.length) { pen.runs[i].path.remove(); pen.runs.splice(i, 1); }
        }
    }

    for (const run of pen.runs) {
        if (!run.dirty) continue;
        run.path.setAttribute("d", buildPathD(run.points));
        run.dirty = false;
    }
}

function frame() {
    rafId = 0;
    const now = performance.now();
    trackPlayback();

    // One inverse per frame element per frame, shared by every pen drawing
    // into it — see the unit note at the top. Inverting per pen would be the
    // same answer at several times the cost. Usually there is one frame (the
    // svg root); a pinned group is a second (see frameFor).
    const frameInverse = new Map();

    for (const [el, pen] of [...pens]) {
        if (!el.isConnected) { stopDrawForElement(el); continue; }
        const root = pen.frame?.isConnected ? pen.frame : el.ownerSVGElement;
        if (!root) continue;

        if (!frameInverse.has(root)) {
            let inv = null;
            try { inv = root.getScreenCTM()?.inverse() ?? null; } catch { /* not rendered */ }
            frameInverse.set(root, inv);
        }

        // anchor:playhead — points are stored relative to the playhead and the
        // layer's shared anchored group is moved to it (once a frame), so the
        // ink scrolls along with the screen
        const shift = pen.cfg.anchor === "playhead" ? playheadOffset() : 0;
        if (pen.cfg.anchor === "playhead") {
            const g = anchoredGroup(pen.layer);
            if (g._shift !== shift) {
                g.setAttribute("transform", `translate(${shift},0)`);
                g._shift = shift;
            }
            // A playhead that leapt (saved position restored at load, a seek, a
            // marker jump) would carry this pen's ink off with it — the points
            // were stored relative to where it WAS. Start the trail afresh.
            if (pen.shiftAt != null && Math.abs(shift - pen.shiftAt) > JUMP_FLOOR * 4) {
                for (const run of pen.runs) run.path.remove();
                pen.runs = [];
                pen.points = 0;
                pen.last = null;
                pen.meanStep = 0;
            }
            pen.shiftAt = shift;
        }

        const m = localMatrixFrom(frameInverse.get(root), el);
        if (m) {
            const x = m.a * pen.cx + m.c * pen.cy + m.e - shift;
            const y = m.b * pen.cx + m.d * pen.cy + m.f;
            if (Number.isFinite(x) && Number.isFinite(y)) {
                // Nothing to record while it is standing still, which is also
                // what keeps a parked, paused or offscreen object free.
                const step = pen.last ? distance(pen.last, { x, y }) : 0;
                if (!pen.last || step >= pen.cfg.min) {
                    // Put there rather than travelled (a looping o2p or trans
                    // returning to its start, a scrub, a rewind): lift, so the
                    // journey home is not drawn across the work.
                    const lift = !!pen.last && isJump(step, pen.meanStep, pen.cfg.jump);
                    addPoint(pen, x, y, now, lift);
                    // The average follows the pen, and starts again after a
                    // lift so the new line is not judged against the old one.
                    pen.meanStep = lift ? 0 : (pen.meanStep ? pen.meanStep * 0.8 + step * 0.2 : step);
                }
            }
        }

        repaint(pen, now);
    }

    if (pens.size) rafId = requestAnimationFrame(frame);
}

function startLoop() {
    if (!rafId && pens.size) rafId = requestAnimationFrame(frame);
}

// ─────────────────────────────────────────────────────────────
//  Entry points
// ─────────────────────────────────────────────────────────────

/**
 * draw() as a control rather than a pen — draw(start:<uid prefix>, clear:1,
 * stop:<uid prefix>, next:…). Pens written with trig:manual wait for this:
 *   start  starts every pen whose uid begins with the prefix (a propagate()
 *          template's pens are <uid>_0, <uid>_1, … so the template's uid, or
 *          any shared stem, starts the lot)
 *   stop   lifts those pens (their ink stays)
 *   clear  clear:1 wipes all ink first; clear:<uid prefix> only those pens'
 *          ink (pens still down go on with a fresh line)
 *   dur    with clear: fade the ink out over this many beats first
 *   next   a cue to run afterwards (cueNext.js)
 */
export function isDrawControl(ast) {
    return (ast?.args || []).some(a => a.type === "start" || a.type === "stop" || a.type === "clear" || a.type === "save");
}

function drawCuesMatching(prefix) {
    const hits = [];
    for (const el of document.querySelectorAll('[data-oscilla*="draw("]')) {
        for (const expr of splitCueId(el.getAttribute("data-oscilla") || "")) {
            if (!/^draw\s*\(/.test(expr)) continue;
            let ast;
            try { ast = parseCueToAST(expr); } catch { continue; }
            if (isDrawControl(ast)) continue;
            const uid = String(ast.args?.find(a => a.type === "uid")?.value ?? "");
            if (uid && uid.startsWith(prefix)) hits.push([ast, el]);
        }
    }
    return hits;
}

// What the control form itself means; everything else it carries is a setting
// for the pens it starts. dur: belongs to clear:, which fades ink out over it.
const CONTROL_KEYS = new Set(["start", "stop", "clear", "save", "dur", "next", "uid", "trig", "tdelay"]);

/** The same cue with these args replacing (or joining) the ones it had. */
function withArgs(ast, extra) {
    const args = (ast.args || []).filter(a => !extra.some(e => e.type === a.type));
    return { ...ast, args: [...args, ...extra] };
}

function handleDrawControl(ast, el) {
    const p = {};
    for (const a of ast.args || []) p[a.type] = String(a.value).replace(/^["'`]+|["'`]+$/g, "");
    // save:1 (or save:<name>) — write every trail of this pass so far to an SVG file
    if (p.save != null && p.save !== "" && !["0", "false"].includes(p.save)) {
        saveDrawings({ name: ["1", "true"].includes(p.save) ? null : p.save, keep: true });
    }
    if (p.clear != null && p.clear !== "") {
        const all = ["1", "true"].includes(p.clear);
        const fadeSecs = Number(secs(p.dur)) || 0;     // beats -> seconds
        if (all && !(fadeSecs > 0)) resetAllDrawings();
        else clearDrawings(all ? null : p.clear, fadeSecs);
    }
    if (p.stop) for (const [, pel] of drawCuesMatching(p.stop)) stopDrawForElement(pel);
    let n = 0;
    // Anything else named here is handed to the pens being started, over
    // whatever they were authored with: draw(start:alpha-, fade:95) sets the
    // same pens drawing again, but as a trail that clears itself. A section
    // that wants a fading blur rather than the permanent arcs an earlier one
    // laid down says so where it starts them, instead of the score carrying
    // two draw() cues per object for the two ways it is drawn.
    const overrides = (ast.args || []).filter(a => !CONTROL_KEYS.has(a.type));
    if (p.start) for (const [past, pel] of drawCuesMatching(p.start)) {
        handleDrawCue(overrides.length ? withArgs(past, overrides) : past, pel);
        n++;
    }
    if (p.start && !n) console.warn(`[draw] start:${p.start} — no draw() pen with a uid starting so`);
    runNext(p.next, el);
    return n;
}

/**
 * Start a pen on this element. Triggering again starts a fresh stroke and
 * leaves the old ink where it is, so repeated passes layer up; a rewind is
 * what clears the page.
 */
export function handleDrawCue(ast, el) {
    if (isDrawControl(ast)) return handleDrawControl(ast, el);
    if (!el) return null;
    // fade: and seg: are beats; the config holds them in seconds. Read here,
    // once, so a control's overrides (withArgs) count the same as the pen's own
    const cfg = parseDrawConfig(ast?.args || [], secPerBeat());
    const frame = frameFor(el);
    const layer = ensureLayer(frame);
    if (!layer) return null;

    // Finish whatever this element was drawing, but keep it on the page.
    const previous = pens.get(el);
    if (previous) finishRuns(previous);

    let cx = 0, cy = 0;
    try {
        const box = el.getBBox();      // once — a wrapper transform cannot change it
        cx = box.x + box.width / 2;
        cy = box.y + box.height / 2;
    } catch { /* not rendered yet; the origin is the best guess available */ }

    const pen = {
        el, cfg, layer, frame,
        cx, cy,
        colour: cfg.colors ? null : inheritedColour(el),
        runs: previous ? previous.runs : [],
        points: previous ? previous.points : 0,
        last: null,                    // a fresh stroke starts a new line
        meanStep: 0,                   // how far it has been moving per point
        step: -1,
        startedAt: performance.now(),
        uid: cfg.uid || el.id || null,
    };
    pens.set(el, pen);
    startLoop();
    return pen;
}

function finishRuns(pen) {
    // The pen put down a point a frame; the curve does not need them all, and
    // every one of them is paid for on each repaint from here on.
    for (const run of pen.runs) {
        if (run.points.length > 2) {
            run.points = simplifyPoints(run.points, SIMPLIFY_TOLERANCE);
            run.path.setAttribute("d", buildPathD(run.points));
        }
        if (pen.cfg.close && run === pen.runs[pen.runs.length - 1] && run.points.length > 2) {
            run.path.setAttribute("d", `${buildPathD(run.points)} Z`);
        }
        archiveRun(pen, run);
    }
    pen.points = pen.runs.reduce((n, r) => n + r.points.length, 0);
}

/** Lift this element's pen. The ink stays. */
export function stopDrawForElement(el) {
    const pen = pens.get(el);
    if (!pen) return false;
    finishRuns(pen);
    pens.delete(el);
    if (!pens.size && rafId) { cancelAnimationFrame(rafId); rafId = 0; }
    return true;
}

/**
 * Wipe the page. The pens stay down: a rewind puts the animations back to
 * their start positions and lets them run again, so the drawing starts over
 * with them. Stopping the pens here instead would mean the piece drew once
 * and never again, since only the load pass starts them.
 */
export function resetAllDrawings() {
    // a rewind ends the pass: whatever it drew goes to the project first
    for (const pen of pens.values()) for (const run of pen.runs) archiveRun(pen, run);
    if (archive.length >= AUTOSAVE_MIN_RUNS) saveDrawings({ reason: "pass" });
    else archive.length = 0;
    let n = 0;
    for (const layer of document.querySelectorAll("g.oscilla-draw-layer")) { layer.replaceChildren(); n++; }
    const now = performance.now();
    for (const pen of pens.values()) {
        pen.runs = [];
        pen.points = 0;
        pen.last = null;        // a new line, not a leap from where it left off
        pen.meanStep = 0;
        pen.step = -1;
        pen.startedAt = now;
    }
    return n;
}

/**
 * Clear the ink of the pens whose uid begins with `prefix` (null: all ink),
 * at once or fading out over `secs`. Pens still down carry on with a fresh
 * line; a pen that was stopped stays stopped. Returns the paths cleared.
 */
export function clearDrawings(prefix = null, secs = 0) {
    const sel = prefix ? `path[data-draw-uid^="${CSS.escape(prefix)}"]` : "path";
    const paths = [...document.querySelectorAll(`g.oscilla-draw-layer ${sel}`)];
    const now = performance.now();
    for (const pen of pens.values()) {
        if (prefix && !String(pen.uid || "").startsWith(prefix)) continue;
        for (const run of pen.runs) archiveRun(pen, run);
        pen.runs = [];
        pen.points = 0;
        pen.last = null;
        pen.meanStep = 0;
        pen.step = -1;
        pen.startedAt = now;
    }
    if (!(secs > 0)) { for (const path of paths) path.remove(); return paths.length; }

    // Fade: gather the paths into one group per parent and fade the groups,
    // so a frame sets a few opacities rather than one per path
    const groups = new Map();
    for (const path of paths) {
        const parent = path.parentNode;
        if (!parent) continue;
        let g = groups.get(parent);
        if (!g) {
            g = document.createElementNS(SVG_NS, "g");
            g.setAttribute("class", "oscilla-draw-fading");
            parent.appendChild(g);
            groups.set(parent, g);
        }
        g.appendChild(path);
    }
    const t0 = performance.now();
    const step = (t) => {
        const k = Math.min(1, (t - t0) / (secs * 1000));
        for (const g of groups.values()) g.setAttribute("opacity", String(1 - k));
        if (k < 1) requestAnimationFrame(step);
        else for (const g of groups.values()) g.remove();
    };
    requestAnimationFrame(step);
    return paths.length;
}

// ─── the pass archive: every trail drawn, kept for export ─────────────────
// The page only ever holds what is on it now: a clear, a fade, max: or a
// rewind throws ink away. Each finished run is copied here when it will not
// be drawn to again (the pen moved on to a new run, lifted, or was cleared),
// so a pass can be written out whole as an SVG — for artwork, printing,
// or another score. Coordinates are the score's own.
const archive = [];
const AUTOSAVE_MIN_RUNS = 20;
// A project switch: the runs kept so far go to the project they were drawn
// in (window.currentProject already names the next one by then), or are
// let go, so the next rewind does not write them into the new project
onReset("project", function leaveDrawings() {
    const leaving = window.currentProjectName;
    if (leaving && archive.length >= AUTOSAVE_MIN_RUNS) saveDrawings({ reason: "project switch", project: leaving });
    archive.length = 0;
});

function archiveRun(pen, run) {
    if (!run || run.points.length < 2) return;
    if (run._archivedLen === run.points.length && run._rec) return;
    let dx = 0, dy = 0;       // anchor:playhead ink sits in a moving group
    const tr = run.path?.parentNode?.getAttribute?.("transform");
    const m = tr && tr.match(/translate\(\s*([-\d.e]+)[ ,]+([-\d.e]+)?/);
    if (m) { dx = Number(m[1]) || 0; dy = Number(m[2]) || 0; }
    const rec = run._rec || {};
    Object.assign(rec, {
        uid: pen.uid || "",
        d: buildPathD(dx || dy ? run.points.map(p => ({ x: p.x + dx, y: p.y + dy })) : run.points),
        stroke: run.path.getAttribute("stroke") || "#000",
        width: run.path.getAttribute("stroke-width") || "1",
        opacity: String(pen.cfg.opacity ?? 1),
        dash: pen.cfg.dash || "",
    });
    if (!run._rec) { run._rec = rec; archive.push(rec); }
    run._archivedLen = run.points.length;
}

function layerKey(uid) {
    const m = String(uid).match(/^(stars|alpha)-([A-Za-z]+)(-heavy)?/);
    if (m) return `${m[2]}${m[1] === "alpha" ? " alpha" : m[3] ? " heavy" : ""}`;
    return String(uid).replace(/_\d+$/, "") || "trails";
}

const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** The pass so far as an SVG document (string), cropped to the ink. */
export function drawingsSvg() {
    for (const pen of pens.values()) for (const run of pen.runs) archiveRun(pen, run);
    if (!archive.length) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of archive) {
        for (const n of r.d.matchAll(/(-?[\d.]+)[ ,](-?[\d.]+)/g)) {
            const x = Number(n[1]), y = Number(n[2]);
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
    }
    const pad = 20;
    const vb = [minX - pad, minY - pad, (maxX - minX) + 2 * pad, (maxY - minY) + 2 * pad].map(v => +v.toFixed(2));
    const layers = new Map();
    for (const r of archive) {
        const k = layerKey(r.uid);
        if (!layers.has(k)) layers.set(k, []);
        layers.get(k).push(`<path d="${esc(r.d)}" fill="none" stroke="${esc(r.stroke)}" stroke-width="${esc(r.width)}" `
            + `stroke-linecap="round" stroke-linejoin="round" opacity="${esc(r.opacity)}"`
            + (r.dash ? ` stroke-dasharray="${esc(r.dash)}"` : "") + ` data-uid="${esc(r.uid)}"/>`);
    }
    const when = new Date().toISOString();
    const body = [...layers].map(([k, paths]) =>
        `<g inkscape:groupmode="layer" inkscape:label="${esc(k)}" id="layer-${esc(k.replace(/\s+/g, "-"))}">\n${paths.join("\n")}\n</g>`).join("\n");
    return `<?xml version="1.0" encoding="UTF-8"?>\n`
        + `<svg xmlns="http://www.w3.org/2000/svg" xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape" `
        + `viewBox="${vb.join(" ")}" width="${vb[2]}" height="${vb[3]}">\n`
        + `<title>${esc(window.currentProject || "oscilla")} — trails ${when}</title>\n`
        + `<desc>Every draw() trail of one pass of the score, in score coordinates (${archive.length} strokes).</desc>\n`
        + `${body}\n</svg>\n`;
}

/**
 * Write the pass to the project as drawings/<name or trails-date>.svg (via the
 * server's project-file route; a download when there is no server). keep:
 * leave the archive as it is (a save part-way through); otherwise the pass
 * ends here and the archive starts again.
 */
export function saveDrawings({ name = null, keep = false, reason = "save", beacon = false, project = window.currentProject } = {}) {
    const svg = drawingsSvg();
    if (!svg) return null;
    const stamp = new Date().toISOString().replace(/[-:]/g, "").replace("T", "-").slice(0, 15);
    const file = `drawings/${String(name || `trails-${stamp}`).replace(/[^\w.-]+/g, "-").replace(/\.svg$/i, "")}.svg`;
    const n = archive.length;
    if (!keep) archive.length = 0;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    if (project) {
        const form = new FormData();
        form.append("path", file);
        form.append("file", blob, file.split("/").pop());
        const url = `/api/project/${encodeURIComponent(project)}/file`;
        if (beacon && navigator.sendBeacon) navigator.sendBeacon(url, form);
        else fetch(url, { method: "POST", body: form })
            .then(r => r.json()).then(j => console.log(j.ok ? `[draw] ${reason}: ${n} trails → ${project}/${file}` : `[draw] save failed: ${j.error}`))
            .catch(err => console.warn("[draw] save failed:", err));
    } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file.split("/").pop();
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    }
    return file;
}

// closing the page ends the pass too
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("pagehide", () => {
        for (const pen of pens.values()) for (const run of pen.runs) archiveRun(pen, run);
        if (archive.length >= AUTOSAVE_MIN_RUNS) saveDrawings({ reason: "pass", beacon: true });
    });
}

/** How many pens are down, for the console and the tests. */
export function activeDrawCount() { return pens.size; }

window.oscillaDraw = { resetAllDrawings, clearDrawings, stopDrawForElement, activeDrawCount, saveDrawings, drawingsSvg };
